import {
    Box,
    Container,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import React, { useContext, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import ResponseGraphs from 'src/components/ResponseGraphs';
import LessonPlanRenderer from '../components/lesson-plan/LessonPlanRenderer';
import { CoachContent } from '../object-actions/types/types';
import { ThemeContext } from '../theme/ThemeContext';
import { markAthleteReceived, markParentReceived } from '../utils/contentHelpers';

interface CoachContentReadOnlyViewProps {
    entity: CoachContent;
    layout?: 'full' | 'compact';
}

const CoachContentReadOnlyView: React.FC<CoachContentReadOnlyViewProps> = ({
    entity,
    layout = 'full'
}) => {
    const theme = useTheme();
    const { brandingSettings } = useContext(ThemeContext);

    // Mark as received when athlete or parent accesses the page (only if not already marked)
    useEffect(() => {
        const markAsReceived = async () => {
            try {
                if (entity.my_roles?.includes('athlete') && !entity.athlete_received) {
                    await markAthleteReceived(Number(entity.id));
                } else if (entity.my_roles?.includes('parent') && !entity.parent_received) {
                    await markParentReceived(Number(entity.id));
                }
            } catch (error) {
                console.error('Error marking content as received:', error);
                // Don't show error to user as this is a background operation
            }
        };

        markAsReceived();
    }, [entity.id, entity.athlete_received, entity.parent_received]);

    if (!entity || entity._type !== 'CoachContent') return null;

    const showHeader = layout === 'full';
    const showResponseGraphs = layout === 'full';
    const isCompact = layout === 'compact';

    const content = (
        <>
            {/* Header */}
            {showHeader && (
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                color: 'secondary.main',
                                mb: 1
                            }}
                        >
                            {entity.title}
                        </Typography>
                        {entity.created_at && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                            >
                                Created on {new Date(entity.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </Typography>
                        )}
                    </Box>

                    {/* Screenshot in header */}
                    {(entity.screenshot_light || entity.screenshot_dark) && (
                        <Box sx={{ flexShrink: 0, maxWidth: 200 }}>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    boxShadow: 2,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    const screenshot = theme.palette.mode === 'dark'
                                        ? entity.screenshot_dark
                                        : entity.screenshot_light;
                                    if (screenshot) {
                                        window.open(screenshot, '_blank');
                                    }
                                }}
                            >
                                <img
                                    src={theme.palette.mode === 'dark' ? (entity.screenshot_dark || '') : (entity.screenshot_light || '')}
                                    alt={`${theme.palette.mode} theme screenshot`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block'
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* ResponseGraphs Section */}
            {showResponseGraphs && entity.athlete && (
                <ResponseGraphs
                    entity={entity.athlete}
                    title="Athlete Performance Overview"
                    height={250}
                />
            )}

            {/* Content Body */}
            <Box sx={{ mb: 4 }}>
                {entity.purpose === 'lesson_plan' && entity.json_body ? (
                    <LessonPlanRenderer entity={entity} />
                ) : entity.body ? (
                    <Box
                        sx={{
                            '& p': { margin: 0 },
                            '& p + p': { marginTop: 2 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                marginTop: 3,
                                marginBottom: 2,
                                fontWeight: 'bold',
                                color: 'text.primary',
                                fontFamily: brandingSettings?.typography?.fontFamily || theme.typography.fontFamily
                            },
                            '& ul, & ol': {
                                marginTop: 2,
                                marginBottom: 2,
                                paddingLeft: 3
                            },
                            '& li': {
                                marginBottom: 1
                            },
                            '& a': {
                                color: theme.palette.primary.main,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            },
                            '& blockquote': {
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                paddingLeft: 2,
                                marginLeft: 0,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                                padding: 2,
                                borderRadius: 1
                            },
                            '& code': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: '2px 4px',
                                borderRadius: 1,
                                fontFamily: 'monospace'
                            },
                            '& pre': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: 2,
                                borderRadius: 1,
                                overflow: 'auto'
                            }
                        }}
                    >
                        <ReactMarkdown>{entity.body}</ReactMarkdown>
                    </Box>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        No content available at this time.
                    </Typography>
                )}
            </Box>
        </>
    );

    if (isCompact) {
        return content;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                {content}
            </Paper>
        </Container>
    );
};

export default CoachContentReadOnlyView;
